import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import BrandSeal from '../components/BrandSeal';

const ForgotPassword = () => {
  const { requestPasswordReset, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const tokenFromUrl = searchParams.get('token') || '';
  const [token, setToken] = useState(tokenFromUrl);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await requestPasswordReset(email);
    if (result.success) {
      setRequestSent(true);
      toast.success(result.data?.message || 'Reset instructions sent if the account exists.');
      if (result.data?.reset_token) {
        setToken(result.data.reset_token);
        toast.info('Debug reset token inserted automatically for local testing.');
      }
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    const result = await resetPassword(token, newPassword);

    if (result.success) {
      toast.success('Password reset successful. You can log in now.');
      navigate('/login');
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="heritage-page min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center">
            <BrandSeal compact />
            <h1 className="heritage-title mt-4 mb-2 text-5xl tracking-[0.08em]">
              Panchami
            </h1>
          </Link>
          <p className="text-[#6a5448]">
            {tokenFromUrl
              ? 'Set your new password below'
              : 'Secure password reset with a one-time token'}
          </p>
        </div>

        <div className="heritage-card rounded-sm p-8">
          {!tokenFromUrl && !requestSent ? (
            <form onSubmit={handleRequestReset} className="space-y-6" data-testid="forgot-password-request-form">
              <div>
                <Label htmlFor="email" className="text-[#1A1A1A]">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="heritage-input mt-2 rounded-none"
                  data-testid="forgot-password-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="heritage-button w-full rounded-none font-medium uppercase tracking-[0.18em]"
                data-testid="forgot-password-request-btn"
              >
                {loading ? 'Sending...' : 'Send Reset Instructions'}
              </Button>
            </form>
          ) : !tokenFromUrl && requestSent ? (
            <div className="space-y-6">
              <div className="rounded-sm border border-[#dcc9ad] bg-[rgba(255,251,243,0.78)] p-4 text-sm text-[#6a5448]">
                Check your email for the reset link. Open that link to set a new password in the new tab.
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full rounded-none border-[#dcc9ad] bg-[rgba(255,251,243,0.78)] hover:bg-[#f3e7d2]"
                onClick={() => {
                  setRequestSent(false);
                  setEmail('');
                }}
              >
                Send Another Reset Link
              </Button>
            </div>
          ) : (
            <form onSubmit={handleConfirmReset} className="space-y-6" data-testid="forgot-password-confirm-form">
              <div className="rounded-sm border border-[#dcc9ad] bg-[rgba(255,251,243,0.78)] p-4 text-sm text-[#6a5448]">
                Your secure reset link is active. Choose a new password below.
              </div>

              <div>
                <Label htmlFor="new-password" className="text-[#1A1A1A]">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="heritage-input mt-2 rounded-none"
                  data-testid="forgot-password-new-password-input"
                />
              </div>

              <div>
                <Label htmlFor="confirm-password" className="text-[#1A1A1A]">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="heritage-input mt-2 rounded-none"
                  data-testid="forgot-password-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="heritage-button w-full rounded-none font-medium uppercase tracking-[0.18em]"
                data-testid="forgot-password-submit-btn"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}

          <p className="mt-6 text-center text-[#6a5448]">
            Remembered it?{' '}
            <Link to="/login" className="text-[#8e755d] hover:underline" data-testid="back-to-login-link">
              Back to login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
